import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Upload, Trash2, Image as ImageIcon, Eye, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

const DEFAULT_SUBJECT = 'Votre simulation SoViewFX – {{projectName}}';
const DEFAULT_BODY = [
  'Bonjour,',
  '',
  'Veuillez trouver ci-joint votre simulation réalisée avec SoViewFX.',
  '',
  '{{message}}',
  '',
  'Bien cordialement,',
  '',
  '{{signature}}',
].join('\n');
const DEFAULT_SIGNATURE = "L'équipe SoViewFX";

const ACCEPTED_FORMATS = ['image/png', 'image/jpeg', 'image/webp'];
const ACCEPTED_FORMATS_LABEL = 'PNG, JPG, JPEG, WEBP';
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const LOGO_ACCEPT_STRING = 'image/png,image/jpeg,image/webp';

function replaceVariables(text, vars) {
  return text.replace(/\{\{(\w+)\}\}/g, (_match, key) =>
    vars[key] !== undefined ? vars[key] : _match
  );
}

export default function EmailTemplateSettings() {
  const navigate = useNavigate();
  const { user, token } = useAuthStore();
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);

  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [signature, setSignature] = useState(DEFAULT_SIGNATURE);
  const [logoUrl, setLogoUrl] = useState(null);

  // ── Auth gate ──────────────────────────────────────────

  useEffect(() => {
    if (!user) {
      navigate('/home');
    }
  }, [user, navigate]);

  // ── Load template on mount ─────────────────────────────

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplate = async () => {
    setPageLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/account/share-email-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 404) {
        // No saved template yet — use defaults
        setSubject(DEFAULT_SUBJECT);
        setBody(DEFAULT_BODY);
        setSignature(DEFAULT_SIGNATURE);
      } else if (res.ok) {
        const data = await res.json();
        setSubject(data.default_subject || DEFAULT_SUBJECT);
        setBody(data.body_text || DEFAULT_BODY);
        setSignature(data.signature_text || DEFAULT_SIGNATURE);
        setLogoUrl(data.logo_url || null);
      } else {
        throw new Error(`Load failed: ${res.status}`);
      }
    } catch (err) {
      console.error('Failed to load email template:', err);
      toast.error('Erreur lors du chargement du template');
      setSubject(DEFAULT_SUBJECT);
      setBody(DEFAULT_BODY);
      setSignature(DEFAULT_SIGNATURE);
    } finally {
      setPageLoading(false);
    }
  };

  // ── Preview data ───────────────────────────────────────

  const previewSignature = useMemo(
    () =>
      replaceVariables(signature, {
        userName: user?.name || 'Utilisateur',
        userEmail: user?.email || 'email@exemple.fr',
      }),
    [signature, user],
  );

  const previewData = useMemo(
    () => ({
      projectName: 'Ma simulation',
      recipientEmail: 'client@exemple.fr',
      userEmail: user?.email || 'email@exemple.fr',
      userName: user?.name || 'Utilisateur',
      message: '[Message personnalisé du projet]',
      signature: previewSignature,
      simulationDate: new Date().toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }),
    }),
    [user, previewSignature],
  );

  const previewSubject = useMemo(
    () => replaceVariables(subject, previewData),
    [subject, previewData],
  );

  const previewBody = useMemo(
    () => replaceVariables(body, previewData),
    [body, previewData],
  );

  // ── Logo upload ────────────────────────────────────────

  const uploadLogo = useCallback(
    async (file) => {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('logo', file);

        const res = await fetch(`${API_URL}/api/account/share-email-template/logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!res.ok) throw new Error('Upload failed');

        const data = await res.json();
        if (data.logo_url) setLogoUrl(data.logo_url);
        toast.success('Logo mis à jour !');
      } catch (err) {
        toast.error('Erreur lors de l\'upload du logo');
      } finally {
        setUploading(false);
      }
    },
    [token],
  );

  const handleLogoSelect = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!ACCEPTED_FORMATS.includes(file.type)) {
        toast.error(`Format non accepté. Formats autorisés : ${ACCEPTED_FORMATS_LABEL}`);
        e.target.value = '';
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error('L\'image est trop volumineuse (max 2 Mo).');
        e.target.value = '';
        return;
      }

      uploadLogo(file);
      e.target.value = '';
    },
    [uploadLogo],
  );

  // ── Logo delete ────────────────────────────────────────

  const handleDeleteLogo = useCallback(async () => {
    if (!window.confirm('Supprimer le logo ?')) return;

    setDeletingLogo(true);
    try {
      const res = await fetch(`${API_URL}/api/account/share-email-template/logo`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Delete failed');

      setLogoUrl(null);
      toast.success('Logo supprimé');
    } catch (err) {
      toast.error('Erreur lors de la suppression du logo');
    } finally {
      setDeletingLogo(false);
    }
  }, [token]);

  // ── Save template ──────────────────────────────────────

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/account/share-email-template`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          default_subject: subject,
          body_text: body,
          signature_text: signature,
        }),
      });

      if (!res.ok) throw new Error('Save failed');

      toast.success('Template enregistré !');
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  }, [token, subject, body, signature]);

  // ── Loading state ──────────────────────────────────────

  if (pageLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="h-14 glass flex items-center px-4 gap-4 flex-shrink-0">
        <button
          onClick={() => navigate('/home')}
          className="p-2 rounded-lg hover:bg-white/5"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-display font-bold text-lg uppercase tracking-wide">
          Modèle d'email de partage
        </h1>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5 pb-safe">
        {/* ── Security notice ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-4 border-yellow-500/20"
          style={{ background: 'rgba(234, 179, 8, 0.04)' }}
        >
          <div className="flex items-start gap-3">
            <Info size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-300/80 leading-relaxed">
              <strong className="text-yellow-300">Sécurité :</strong> Les champs ci-dessus sont
              textuels uniquement. Le HTML n'est pas autorisé. Le serveur convertit automatiquement
              votre texte en email HTML sécurisé.
            </p>
          </div>
        </motion.div>

        {/* ── 1. Subject ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="card p-5"
        >
          <label className="block text-sm font-medium text-white mb-2">
            Objet par défaut
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="input"
            placeholder={DEFAULT_SUBJECT}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Variables disponibles : {'{{projectName}}'}, {'{{recipientEmail}}'}, {'{{userName}}'}
          </p>
        </motion.div>

        {/* ── 2. Body ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-5"
        >
          <label className="block text-sm font-medium text-white mb-2">
            Corps du message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="input min-h-[150px] resize-y pt-3"
            placeholder={DEFAULT_BODY}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Variables disponibles : {'{{message}}'}, {'{{signature}}'}, {'{{projectName}}'},{' '}
            {'{{userName}}'}, {'{{simulationDate}}'}
          </p>
        </motion.div>

        {/* ── 3. Signature ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card p-5"
        >
          <label className="block text-sm font-medium text-white mb-2">
            Signature
          </label>
          <textarea
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            className="input min-h-[80px] resize-y pt-3"
            placeholder={DEFAULT_SIGNATURE}
          />
          <p className="text-xs text-muted-foreground mt-2">
            Variables disponibles : {'{{userName}}'}, {'{{userEmail}}'}
          </p>
        </motion.div>

        {/* ── 4. Logo ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-5"
        >
          <label className="block text-sm font-medium text-white mb-3">
            Logo de l'email{' '}
            <span className="text-muted-foreground font-normal">(optionnel)</span>
          </label>

          {logoUrl ? (
            /* Logo exists — show preview + replace/delete buttons */
            <div className="space-y-3">
              <div className="flex items-center gap-4 flex-wrap">
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="max-h-20 rounded border border-white/10"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => replaceInputRef.current?.click()}
                    disabled={uploading || deletingLogo}
                    className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5"
                  >
                    {uploading ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Upload size={14} />
                    )}
                    Remplacer
                  </button>
                  <button
                    onClick={handleDeleteLogo}
                    disabled={deletingLogo || uploading}
                    className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    {deletingLogo ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Trash2 size={14} />
                    )}
                    Supprimer
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Formats acceptés : {ACCEPTED_FORMATS_LABEL} — Max 2 Mo
              </p>
            </div>
          ) : (
            /* No logo — show upload area */
            <div>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full h-24 rounded-2xl border-2 border-dashed border-white/10 bg-white/5 hover:border-primary/50 hover:bg-white/10 flex flex-col items-center justify-center gap-2 transition-all"
              >
                {uploading ? (
                  <Loader2 size={24} className="animate-spin text-muted-foreground" />
                ) : (
                  <>
                    <ImageIcon size={24} className="text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Cliquez pour ajouter un logo</p>
                  </>
                )}
              </button>
              <p className="text-xs text-muted-foreground mt-2">
                Formats acceptés : {ACCEPTED_FORMATS_LABEL} — Max 2 Mo. Le logo apparaîtra dans
                l'en-tête de l'email.
              </p>
            </div>
          )}

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept={LOGO_ACCEPT_STRING}
            className="hidden"
            onChange={handleLogoSelect}
          />
          <input
            ref={replaceInputRef}
            type="file"
            accept={LOGO_ACCEPT_STRING}
            className="hidden"
            onChange={handleLogoSelect}
          />
        </motion.div>

        {/* ── 5. Preview ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card p-5"
        >
          <div className="flex items-center gap-2 mb-3">
            <Eye size={16} className="text-primary" />
            <label className="text-sm font-medium text-white">Aperçu du rendu</label>
          </div>

          {/* Email preview card */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3">
            {/* Logo in email */}
            {logoUrl && (
              <div className="border-b border-white/10 pb-3">
                <img src={logoUrl} alt="Logo" className="max-h-10 rounded" />
              </div>
            )}

            {/* Subject */}
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">
                Objet
              </p>
              <p className="text-sm text-white font-medium">{previewSubject}</p>
            </div>

            {/* Divider */}
            <div className="border-t border-white/10" />

            {/* Body */}
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">
                Corps du message
              </p>
              <div className="text-sm text-white/90 whitespace-pre-line leading-relaxed">
                {previewBody}
              </div>
            </div>

            {/* Signature (also shown separately so user can gauge it) */}
            {previewSignature && (
              <div className="border-t border-white/10 pt-3">
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">
                  Signature
                </p>
                <div className="text-sm text-white/80 whitespace-pre-line">
                  {previewSignature}
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* ── 6. Save button ── */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary w-full flex items-center justify-center gap-2 py-4"
          >
            {saving ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Save size={20} />
            )}
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
